// The Camera tab's corrections. Each switch and slider writes to the sidecar over the
// session's remote-control channel, and the lit side of a switch is the state this
// panel last sent: the pipeline cannot be read back.

// Stored calibration the sidecar can put back, and the elements it can switch.
const CORRECTIONS = ["distortion_model", "homography", "vignetting_correction"];
const ELEMENTS = ["denoiser", "demoire"];

const HOMOGRAPHY_PRESETS = {
  Identity: {
    h00: 1.0,
    h01: 0.0,
    h02: 0.0,
    h10: 0.0,
    h11: 1.0,
    h12: 0.0,
    h20: 0.0,
    h21: 0.0,
    h22: 1.0,
  },
  ZoomOut: {
    h00: 0.5,
    h01: 0.0,
    h02: 0.25,
    h10: 0.0,
    h11: 0.5,
    h12: 0.25,
    h20: 0.0,
    h21: 0.0,
    h22: 1.0,
  },
  ZoomIn: {
    h00: 1.5,
    h01: 0.0,
    h02: -0.25,
    h10: 0.0,
    h11: 1.5,
    h12: -0.25,
    h20: 0.0,
    h21: 0.0,
    h22: 1.0,
  },
  Rotate90: {
    h00: 0.0,
    h01: -1.0,
    h02: 960,
    h10: 1.0,
    h11: 0.0,
    h12: -960,
    h20: 0.0,
    h21: 0.0,
    h22: 1.0,
  },
  Rotate45: {
    h00: 0.7071,
    h01: -0.7071,
    h02: 960,
    h10: 0.7071,
    h11: 0.7071,
    h12: -960,
    h20: 0.0,
    h21: 0.0,
    h22: 1.0,
  },
  Skew: {
    h00: 1.0,
    h01: 0.3,
    h02: 0.0,
    h10: 0.3,
    h11: 1.0,
    h12: 0.0,
    h20: 0.0001,
    h21: 0.0001,
    h22: 1.0,
  },
  Perspective: {
    h00: 1.0,
    h01: 0.0,
    h02: 0.0,
    h10: 0.0,
    h11: 1.0,
    h12: 0.0,
    h20: 0.0005,
    h21: 0.0002,
    h22: 1.0,
  },
  Legacy: {
    h00: 1.3032250027964913,
    h01: -0.09462283211737542,
    h02: 833.9679493644677,
    h10: 0.0011345106742600556,
    h11: 1.2724003162329587,
    h12: 471.3314891049412,
    h20: -7.791504983134255e-6,
    h21: -3.7729095080353e-5,
    h22: 0.9808513446745678,
  },
};

const lens = (fx, fy, cx, cy, k1, k2, k3, k4, lens_model) => ({
  fx,
  fy,
  cx,
  cy,
  k1,
  k2,
  k3,
  k4,
  lens_model,
});

const LENS_PRESETS = {
  Equidistant: lens(1050, 1050, 960, 540, 0.0, 0.0, 0.0, 0.0, "equidistant"),
  Perspective: lens(1050, 1050, 960, 540, -0.3, 0.0, 0.0, 0.0, "perspective"),
  Orthographic: lens(1050, 1050, 960, 540, -0.3, 0.0, 0.0, 0.0, "orthographic"),
  Stereographic: lens(1050, 1050, 960, 540, 0.3, 0.0, 0.0, 0.0, "stereographic"),
  Equisolid: lens(1050, 1050, 960, 540, 0.3, 0.0, 0.0, 0.0, "equisolid"),
  "e-CON": lens(
    640.0244116242908,
    640.6073224855631,
    964.6754588353931,
    594.6080592724254,
    0.23634005663119811,
    0.488101067186476,
    -0.4784404598448789,
    0.180376880890512,
    "perspective",
  ),
  "Rokinon FE75MFT": lens(
    806.9338359737485,
    806.262431744419,
    948.2805634981071,
    547.3677679389784,
    -0.011471216865893835,
    -0.007111452987309034,
    0.010281834316738276,
    -0.005961512364417588,
    "perspective",
  ),
  // The Example presets leave the lens model as it is.
  "Example 1": {
    cx: 960,
    cy: 540,
    fx: 1000,
    fy: 1000,
    k1: -0.011471216865893835,
    k2: -0.007111452987309034,
    k3: 0.010281834316738276,
    k4: -0.005961512364417588,
  },
  "Example 2": { cx: 960, cy: 540, fx: 1000, fy: 1000, k1: 1.0, k2: 0.0, k3: 0.0, k4: 0.0 },
  "Example 3": { cx: 960, cy: 540, fx: 1050, fy: 1050, k1: 0.8, k2: 0.2, k3: 0.0, k4: 0.0 },
};

const FLAT_FIELD_PRESETS = {
  Mild: { cx: 960, cy: 540, f1: 1.0, f2: 0.0, f3: 0.000005, f4: 0.0 },
  Medium: { cx: 960, cy: 540, f1: 1.0, f2: 0.0, f3: 0.00001, f4: 0.0 },
  Strong: { cx: 960, cy: 540, f1: 1.0, f2: 0.0, f3: 0.00005, f4: 0.0 },
};

const PRESETS = {
  homography: HOMOGRAPHY_PRESETS,
  distortion_model: LENS_PRESETS,
  vignetting_correction: FLAT_FIELD_PRESETS,
};

// What the sliders show before anything is sent; the sidecar's stored values are not read.
function initialParams() {
  return {
    // Start on perspective, the model the sidecar's lens profiles are fitted for.
    homography: { ...HOMOGRAPHY_PRESETS.Identity },
    distortion_model: lens(1050, 1050, 960, 540, 0.0, 0.0, 0.0, 0.0, "perspective"),
    vignetting_correction: { cx: 960, cy: 540, f1: 1.0, f2: 0.0, f3: 0.0, f4: 0.0 },
  };
}

export function formatParam(group, param, value) {
  if (group === "distortion_model") {
    return param.startsWith("k") ? value.toFixed(4) : value.toFixed(2);
  }

  if (group === "homography") {
    if (param === "h02" || param === "h12") return value.toFixed(2);
    if (param === "h20" || param === "h21") return value === 0 ? "0" : value.toExponential(2);
    return value.toFixed(4);
  }

  if (param === "cx" || param === "cy") return String(Math.round(value));
  return param === "f3" ? value.toFixed(6) : value.toFixed(2);
}

export const ProcessorController = {
  // ==========================================
  // Lifecycle Methods
  // ==========================================
  mounted() {
    this.params = initialParams();
    // "on" or "off" per correction and element, absent until this panel sends one.
    this.states = {};

    this.remoteControl = window.sauronRemoteControl || null;
    this.handleRemoteControlReady = (event) => {
      this.remoteControl = event.detail.remoteControl;
    };
    window.addEventListener("remoteControlReady", this.handleRemoteControlReady);

    this.handleClick = (event) => this.onClick(event);
    this.handleInput = (event) => this.onInput(event);
    this.handleChange = (event) => this.onChange(event);
    this.el.addEventListener("click", this.handleClick);
    this.el.addEventListener("input", this.handleInput);
    this.el.addEventListener("change", this.handleChange);

    for (const group of CORRECTIONS) this.syncSliders(group);
    this.syncLensModel();

    // tvl-demoire always starts off, and cannot be switched on without a carrier file.
    this.light("demoire", "off");
  },

  destroyed() {
    window.removeEventListener("remoteControlReady", this.handleRemoteControlReady);
    this.el.removeEventListener("click", this.handleClick);
    this.el.removeEventListener("input", this.handleInput);
    this.el.removeEventListener("change", this.handleChange);
  },

  // ==========================================
  // Event Handlers
  // ==========================================
  onClick(event) {
    const button = event.target.closest("button[data-value]");
    const group = button && button.closest("[data-switch]");
    if (!group || button.disabled) return;

    // A switch sits in its card's summary, which would otherwise open or close the card.
    event.preventDefault();
    this.handleSwitch(group.dataset.switch, button.dataset.value);
  },

  onInput(event) {
    const slider = event.target.closest("input[data-group]");
    if (!slider) return;

    const { group, param } = slider.dataset;
    this.params[group][param] = parseFloat(slider.value);
    this.showOutput(group, param);
    this.clearPreset(group);
    this.sendGroup(group);
  },

  onChange(event) {
    const select = event.target;

    if (select.matches("select[data-preset]")) {
      this.applyPreset(select.dataset.preset, select.value);
    } else if (select.matches("select[data-lens-model]")) {
      this.params.distortion_model.lens_model = select.value;
      this.clearPreset("distortion_model");
      this.sendGroup("distortion_model");
    }
  },

  handleSwitch(name, value) {
    switch (name) {
      case "all":
        return this.setAll(value);
      case "grid":
        return this.sentAs("grid", value, this.sendGridUpdate(value === "true"));
      case "interpolation":
        return this.sentAs("interpolation", value, this.sendInterpolationUpdate(value));
      default:
        if (CORRECTIONS.includes(name)) {
          return this.sentAs(name, value, this.sendCorrectionState(name, value));
        }
        if (ELEMENTS.includes(name)) {
          return this.sentAs(name, value, this.sendCameraElementState(name, value));
        }
    }
  },

  // Every correction and element at once, in one message.
  setAll(state) {
    const switchable = ELEMENTS.filter((element) => this.canSwitch(element, state));
    const message =
      state === "on"
        ? { restore: CORRECTIONS }
        : Object.fromEntries(CORRECTIONS.map((correction) => [correction, "none"]));

    if (switchable.length > 0) {
      message.element_enabled = Object.fromEntries(
        switchable.map((element) => [element, state === "on"]),
      );
    }

    if (!this.sendRemoteControlMessage(message)) return this.showSent(false);

    for (const name of [...CORRECTIONS, ...switchable]) this.light(name, state);
    this.showSent(true);
  },

  canSwitch(element, state) {
    return this.hasEnabledSide(element, state) && this.states[element] !== state;
  },

  // A side that is missing or disabled cannot be sent, as moiré On today.
  hasEnabledSide(name, state) {
    const button = this.el.querySelector(`[data-switch="${name}"] [data-value="${state}"]`);
    return !!button && !button.disabled;
  },

  applyPreset(group, name) {
    const preset = PRESETS[group][name];
    if (!preset) return;

    const params = this.params[group];
    if (group === "distortion_model" && !preset.lens_model) {
      Object.assign(params, preset, { lens_model: params.lens_model });
    } else {
      Object.assign(params, preset);
    }

    this.syncSliders(group);
    if (group === "distortion_model") this.syncLensModel();
    this.sendGroup(group);
  },

  // ==========================================
  // Remote Control Communication Methods
  // ==========================================
  sendRemoteControlMessage(data) {
    if (!this.remoteControl || this.remoteControl.readyState !== "open") {
      return false;
    }

    this.remoteControl.send(
      JSON.stringify({
        type: "update_pipeline",
        ...data,
      }),
    );

    return true;
  },

  // Off, or back to the device's calibration.
  sendCorrectionState(correction, state) {
    if (state === "on") {
      return this.sendRemoteControlMessage({ restore: [correction] });
    }

    return this.sendRemoteControlMessage({ [correction]: "none" });
  },

  // Denoise and moiré: switched on or off in the running pipeline.
  sendCameraElementState(element, state) {
    return this.sendRemoteControlMessage({ element_enabled: { [element]: state === "on" } });
  },

  // A slider or preset sets the correction, which switches it on.
  sendGroup(group) {
    const sent =
      group === "homography"
        ? this.sendHomographyUpdate()
        : group === "distortion_model"
          ? this.sendFisheyeUpdate()
          : this.sendVignettingUpdate();

    this.sentAs(group, "on", sent);
  },

  sendHomographyUpdate() {
    const homography = {
      matrix: Object.fromEntries(
        ["h00", "h01", "h02", "h10", "h11", "h12", "h20", "h21", "h22"].map((key) => [
          key,
          this.params.homography[key],
        ]),
      ),
    };

    return this.sendRemoteControlMessage({ homography });
  },

  sendFisheyeUpdate() {
    const { fx, fy, cx, cy, k1, k2, k3, k4, lens_model } = this.params.distortion_model;

    return this.sendRemoteControlMessage({
      distortion_model: {
        matrix: { cx, cy, fx, fy },
        distortion: { k1, k2, k3, k4 },
        lens_model,
      },
    });
  },

  sendVignettingUpdate() {
    const { cx, cy, f1, f2, f3, f4 } = this.params.vignetting_correction;

    return this.sendRemoteControlMessage({
      vignetting_correction: { cx, cy, factors: [f1, f2, f3, f4] },
    });
  },

  sendGridUpdate(grid) {
    return this.sendRemoteControlMessage({ grid });
  },

  sendInterpolationUpdate(method) {
    return this.sendRemoteControlMessage({ interpolation_method: method });
  },

  // ==========================================
  // UI Update Methods
  // ==========================================
  // Lights a switch only when its message went out.
  sentAs(name, value, sent) {
    if (sent) this.light(name, value);
    this.showSent(sent);
  },

  light(name, value) {
    if (CORRECTIONS.includes(name) || ELEMENTS.includes(name)) this.states[name] = value;

    const group = this.el.querySelector(`[data-switch="${name}"]`);
    if (group) {
      group.querySelectorAll("[data-value]").forEach((button) => {
        button.dataset.active = String(button.dataset.value === value);
      });
    }

    this.lightAll();
  },

  // All is lit On when every correction and element that can be switched on is on, and
  // Off when every one is off.
  lightAll() {
    const names = [...CORRECTIONS, ...ELEMENTS];
    const switchableOn = names.filter((name) => this.hasEnabledSide(name, "on"));
    const all = switchableOn.every((name) => this.states[name] === "on")
      ? "on"
      : names.every((name) => this.states[name] === "off")
        ? "off"
        : null;
    const group = this.el.querySelector('[data-switch="all"]');

    if (group) {
      group.querySelectorAll("[data-value]").forEach((button) => {
        button.dataset.active = String(button.dataset.value === all);
      });
    }
  },

  showSent(sent) {
    const status = this.el.querySelector("[data-camera-status]");
    if (status) status.classList.toggle("hidden", sent);
  },

  showOutput(group, param) {
    const output = this.el.querySelector(`[data-output="${group}.${param}"]`);
    if (output) output.textContent = formatParam(group, param, this.params[group][param]);
  },

  syncSliders(group) {
    this.el.querySelectorAll(`input[data-group="${group}"]`).forEach((slider) => {
      slider.value = this.params[group][slider.dataset.param];
      this.showOutput(group, slider.dataset.param);
    });
  },

  syncLensModel() {
    const select = this.el.querySelector("select[data-lens-model]");
    if (select) select.value = this.params.distortion_model.lens_model;
  },

  // The sliders no longer match the preset that was chosen.
  clearPreset(group) {
    const select = this.el.querySelector(`select[data-preset="${group}"]`);
    if (select) select.value = "";
  },
};
